/**
 * v1.2 Batch 4-A：企业微信会话存档配置
 *
 * /admin/settings/wework-archive
 * - 4 个凭证字段（corp_id / secret / private_key PEM / 总开关 / 拉取间隔）
 * - [测试连通性] / [立即拉取一次] / 最近 20 条拉取日志
 * - 密码态字段 mask 显示 + 永不写日志（前端只展示 hasSecret/hasPrivateKey 布尔）
 */
import { useEffect, useState } from 'react';
import { Input, message, Switch, InputNumber } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { PageHeader, HomeCard, Btn, Pill, TableWrap } from '../../components/ui';
import { fmtDateTime } from '../../lib/format';

interface Status {
  enabled: boolean;
  configured: boolean;
  switchedOn: boolean;
  corpIdMasked: string;
  hasSecret: boolean;
  hasPrivateKey: boolean;
  cursorSeq: number;
  cronMinutes: number;
}

export function WeworkArchivePage({ embedded = false }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const { data: status, refetch } = useQuery<Status>({
    queryKey: ['wework-archive-status'],
    queryFn: () => $.get('/admin/wework-archive/status'),
  });
  const { data: logs } = useQuery<Array<{ at: string; ok: boolean; msg: string }>>({
    queryKey: ['wework-archive-logs'],
    queryFn: () => $.get('/admin/wework-archive/logs'),
    refetchInterval: 10_000,
  });

  const [corpId, setCorpId] = useState('');
  const [secret, setSecret] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [cronMinutes, setCronMinutes] = useState(60);

  useEffect(() => {
    if (!status) return;
    setEnabled(status.switchedOn);
    setCronMinutes(status.cronMinutes);
  }, [status]);

  const save = useMutation({
    mutationFn: () => $.post('/admin/wework-archive/config', {
      corpId: corpId || undefined,
      secret: secret || undefined,
      privateKey: privateKey || undefined,
      enabled,
      cronMinutes,
    }),
    onSuccess: () => {
      message.success('已保存');
      setCorpId(''); setSecret(''); setPrivateKey('');
      qc.invalidateQueries({ queryKey: ['wework-archive-status'] });
    },
    onError: (e: any) => message.error(e?.message ?? '保存失败'),
  });

  // v1.2-rc2 #1：补 onError + loading 显式提示
  // 根因：backend testConnection 返 {ok:false,message:'凭证不全'}，
  // 被 frontend api 拦截器 reject 走 onError；之前没写 onError → 静默无 toast
  const test = useMutation({
    mutationFn: () => $.post('/admin/wework-archive/test', {}),
    onMutate: () => { message.loading({ content: '正在测试连通性…', key: 'wework-test', duration: 0 }); },
    onSuccess: (r: any) => {
      message.destroy('wework-test');
      message.success(r?.message ?? '连接正常');
    },
    onError: (e: any) => {
      message.destroy('wework-test');
      message.warning(e?.message ?? '测试失败');
    },
  });

  const pullOnce = useMutation({
    mutationFn: () => $.post('/admin/wework-archive/pull', {}),
    onMutate: () => { message.loading({ content: '正在拉取会话…', key: 'wework-pull', duration: 0 }); },
    onSuccess: (r: any) => {
      message.destroy('wework-pull');
      message.success(`拉取成功，新增 ${r.fetched ?? 0} 条`);
      refetch();
      qc.invalidateQueries({ queryKey: ['wework-archive-logs'] });
    },
    onError: (e: any) => {
      message.destroy('wework-pull');
      message.warning(`拉取失败：${e?.message ?? '未知错误'}`);
      refetch();
      qc.invalidateQueries({ queryKey: ['wework-archive-logs'] });
    },
  });

  if (!status) return null;

  // v1.2-rc2 ⑥：embedded 模式（在 ControlPanel tab 内渲染），不显示 PageHeader
  const actionsBar = (
    <div style={{ display: 'flex', gap: 8, marginBottom: 16, justifyContent: 'flex-end' }}>
      <Btn kind="default" onClick={() => test.mutate()} disabled={test.isPending}>
        {test.isPending ? '测试中…' : '测试连通性'}
      </Btn>
      <Btn kind="primary" onClick={() => pullOnce.mutate()} disabled={!status.enabled || pullOnce.isPending}>
        {pullOnce.isPending ? '拉取中…' : '立即拉取一次'}
      </Btn>
    </div>
  );
  return (
    <>
      {!embedded && (
        <PageHeader
          breadcrumb={['Admin', '企微会话存档']}
          title="企业微信会话存档"
          subtitle="教务对接学员的企微聊天自动拉取 + 解密 → 写入 academic_conversation_archives，给 Batch 4-B AI 总结当主数据源"
          actions={
            <>
              <Btn kind="default" onClick={() => test.mutate()} disabled={test.isPending}>
                {test.isPending ? '测试中…' : '测试连通性'}
              </Btn>
              <Btn kind="primary" onClick={() => pullOnce.mutate()} disabled={!status.enabled || pullOnce.isPending}>
                {pullOnce.isPending ? '拉取中…' : '立即拉取一次'}
              </Btn>
            </>
          }
        />
      )}
      <div className={embedded ? '' : 'work-content'}>
        {embedded && actionsBar}
        <HomeCard title="状态">
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
            <KV label="总状态" value={
              <Pill tone={status.enabled ? 'success' : 'warning'}>
                {status.enabled ? '✓ 已启用 · 凭证齐全' : status.configured ? '⏸ 凭证已填，开关未开' : '⚠ 未启用 · 凭证不全'}
              </Pill>
            } />
            <KV label="Corp ID" value={status.corpIdMasked || '—'} />
            <KV label="Secret" value={status.hasSecret ? '✓ 已配置' : '✗ 未配置'} />
            <KV label="RSA 私钥" value={status.hasPrivateKey ? '✓ 已配置' : '✗ 未配置'} />
            <KV label="拉取游标 seq" value={status.cursorSeq} />
            <KV label="cron 间隔" value={`${status.cronMinutes} 分钟`} />
          </div>
        </HomeCard>

        <HomeCard title="凭证配置（密码态字段保存后清空显示）">
          <div style={{ display: 'grid', gap: 12 }}>
            <Field label="企业 ID（corp_id）">
              <Input value={corpId} onChange={(e) => setCorpId(e.target.value)}
                     placeholder={status.corpIdMasked || '例：ww1234567890abcdef'} />
            </Field>
            <Field label="会话存档应用 secret">
              <Input.Password value={secret} onChange={(e) => setSecret(e.target.value)}
                              placeholder={status.hasSecret ? '已配置（留空保持原值）' : '应用 secret'} />
            </Field>
            <Field label="RSA 私钥 PEM">
              <Input.TextArea value={privateKey} onChange={(e) => setPrivateKey(e.target.value)}
                              rows={4}
                              placeholder={status.hasPrivateKey ? '已配置（留空保持原值）' : '-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----'}
                              style={{ fontFamily: 'monospace', fontSize: 12 }} />
            </Field>
            <Field label="总开关">
              <Switch checked={enabled} onChange={setEnabled} />
              <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-3, #8F959E)' }}>
                关闭后 cron 不再拉取（凭证保留）
              </span>
            </Field>
            <Field label="cron 拉取间隔（分钟）">
              <InputNumber value={cronMinutes} onChange={(v) => setCronMinutes(Number(v ?? 60))}
                           min={5} max={1440} style={{ width: 200 }} />
            </Field>
            <div>
              <Btn kind="primary" disabled={save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? '保存中…' : '保存配置'}
              </Btn>
            </div>
          </div>
        </HomeCard>

        <HomeCard title="最近 20 次拉取日志">
          <TableWrap>
            <table className="ck-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: 160 }}>时间</th>
                  <th style={{ width: 80 }}>结果</th>
                  <th>信息</th>
                </tr>
              </thead>
              <tbody>
                {(logs ?? []).map((l, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: 12, fontFamily: 'monospace' }}>
                      {fmtDateTime(l.at)}
                    </td>
                    <td>
                      <Pill tone={l.ok ? 'success' : 'danger'}>
                        {l.ok ? '✓ 成功' : '✗ 失败'}
                      </Pill>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-2, #4E5969)' }}>{l.msg}</td>
                  </tr>
                ))}
                {(!logs || logs.length === 0) && (
                  <tr><td colSpan={3} style={{ textAlign: 'center', padding: 20, color: 'var(--text-3, #8F959E)' }}>
                    暂无拉取记录
                  </td></tr>
                )}
              </tbody>
            </table>
          </TableWrap>
        </HomeCard>

        <HomeCard title="上线 checklist">
          <ol style={{ fontSize: 13, lineHeight: 1.8, color: 'var(--text-2, #4E5969)', paddingLeft: 20 }}>
            <li>backend npm install node-wxwork-finance-sdk（C++ addon，需 node-gyp）</li>
            <li>替换 WeworkArchiveService.fetchOnce 中的 SDK 占位调用</li>
            <li>在企微管理后台开启「会话存档」app + 下载 RSA 私钥 + 配置可信 IP</li>
            <li>本页填入 corp_id / secret / PEM 私钥 → 打开总开关 → [测试连通性]</li>
            <li>[立即拉取一次] 验证写入 academic_conversation_archives + 解密成功</li>
            <li>cron 每小时自动拉，游标 seq 自动维护</li>
          </ol>
        </HomeCard>
      </div>
    </>
  );
}

function KV({ label, value }: { label: string; value: any }) {
  return (
    <div style={{ minWidth: 140 }}>
      <div style={{ fontSize: 11, color: 'var(--text-3, #8F959E)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: 'var(--text-1, #1F2329)' }}>{value}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div style={{ width: 160, fontSize: 13, color: 'var(--text-2, #4E5969)', paddingTop: 6 }}>{label}</div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}
